/**
 * Dynamic Navigation Hook
 * 
 * Fetches navigation items from the database to enable true CMS functionality.
 * Pages are automatically included in navigation when marked as show_in_nav=true.
 * Also includes feature-specific routes (news, events, gallery, etc.) when enabled.
 */

import { useState, useEffect } from 'react';
import { safeFrom, isSupabaseAvailable } from '@/lib/safe-supabase';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export interface NavigationItem {
  name: string;
  path: string;
  order: number;
}

/**
 * Hook to fetch navigation items dynamically from database
 * Falls back to static navigation if database is unavailable
 * Includes feature-specific routes when features are enabled
 */
export const useNavigationItems = () => {
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { features } = useSiteSettings();

  useEffect(() => {
    const fetchNavigationItems = async () => {
      setLoading(true);
      setError(null);

      try {
        // Check if Supabase is available
        if (!isSupabaseAvailable()) {
          console.warn('Supabase not available, using fallback navigation');
          setNavigationItems(getFallbackNavigation(features));
          setLoading(false);
          return;
        }

        // Fetch navigation items from database
        // Note: Using existing columns only, navigation fields will be added via migration
        const response = safeFrom('pages')
          .select('slug, title, is_published')
          .eq('is_published', true) // Only show published pages
          .order('title', { ascending: true });

        const { data, error: fetchError } = await response;

        if (fetchError) {
          console.warn('Failed to fetch navigation from database:', fetchError);
          setNavigationItems(getFallbackNavigation(features));
          setError('Failed to load navigation from database');
        } else {
          // Transform database results to navigation format
          const dbNavItems: NavigationItem[] = [
            // Always include Home first
            { name: 'Home', path: '/', order: 0 }
          ];

          // Add database pages
          if (data) {
            // Filter and order pages intelligently based on common navigation patterns
            const navigationPages = data.filter(page => {
              // Include main navigation pages, exclude admin/utility pages
              // Exclude "about" slug - only use "about-us" (CMS-managed page)
              // Exclude feature-specific slugs that have dedicated routes
              return page.is_published && 
                     !['privacy', 'accessibility', 'terms', 'policy', 'about', 'news', 'events', 'gallery'].includes(page.slug);
            });

            navigationPages.forEach(page => {
              // Smart ordering based on typical navigation structure
              let order = 100;
              switch (page.slug) {
                case 'about-us':
                case 'about':
                  order = 10;
                  break;
                case 'services': order = 20; break;
                case 'programs': order = 30; break;
                case 'projects': order = 60; break;
                case 'contact': order = 70; break;
                default: order = 80; break; // New pages appear before contact
              }

              // Smart title formatting
              const displayName = page.title
                .replace(/^(Our|The)\s+/i, '') // Remove common prefixes
                .replace(/\s+(Page|Section)$/i, ''); // Remove common suffixes

              dbNavItems.push({
                name: displayName,
                path: page.slug === 'about' ? '/about-us' : `/${page.slug}`,
                order
              });
            });
          }

          // Add feature-specific routes when enabled
          if (features.news?.enabled) {
            dbNavItems.push({ name: 'News', path: '/news', order: 40 });
          }
          if (features.events?.enabled) {
            dbNavItems.push({ name: 'Events', path: '/events', order: 45 });
          }
          if (features.gallery?.enabled) {
            dbNavItems.push({ name: 'Gallery', path: '/gallery', order: 50 });
          }

          // Sort by order, then by name
          dbNavItems.sort((a, b) => {
            if (a.order !== b.order) return a.order - b.order;
            return a.name.localeCompare(b.name);
          });
          
          setNavigationItems(dbNavItems);
        }
      } catch (err) {
        console.warn('Error fetching navigation:', err);
        setNavigationItems(getFallbackNavigation(features));
        setError('Unexpected error loading navigation');
      } finally {
        setLoading(false);
      }
    };

    fetchNavigationItems();
  }, [features]);

  return {
    navigationItems,
    loading,
    error,
    refetch: () => {
      // Allow manual refresh of navigation
      setLoading(true);
      const fetchNavigationItems = async () => {
        // Re-run the fetch logic
        setLoading(true);
        setError(null);

        try {
          if (!isSupabaseAvailable()) {
            setNavigationItems(getFallbackNavigation());
            setLoading(false);
            return;
          }

          const response = safeFrom('pages')
            .select('slug, title, is_published')
            .eq('is_published', true)
            .order('title', { ascending: true });

          const { data, error: fetchError } = await response;

          if (fetchError) {
            setNavigationItems(getFallbackNavigation(features));
            setError('Failed to load navigation from database');
          } else {
            const dbNavItems: NavigationItem[] = [
              { name: 'Home', path: '/', order: 0 }
            ];

            if (data) {
              const navigationPages = data.filter(page => {
                // Exclude "about" slug - only use "about-us" (CMS-managed page)
                // Exclude feature-specific slugs that have dedicated routes
                return page.is_published && 
                       !['privacy', 'accessibility', 'terms', 'policy', 'about', 'news', 'events', 'gallery'].includes(page.slug);
              });

              navigationPages.forEach(page => {
                let order = 100;
                switch (page.slug) {
                  case 'about-us':
                  case 'about':
                    order = 10;
                    break;
                  case 'services': order = 20; break;
                  case 'programs': order = 30; break;
                  case 'projects': order = 60; break;
                  case 'contact': order = 70; break;
                  default: order = 80; break;
                }

                const displayName = page.title
                  .replace(/^(Our|The)\s+/i, '')
                  .replace(/\s+(Page|Section)$/i, '');

                // Ensure slug is valid and page exists
                if (!page.slug || page.slug.trim() === '') {
                  console.warn('Page missing slug, skipping:', page);
                  return;
                }

                // Build the path - handle special cases
                let path = `/${page.slug}`;
                if (page.slug === 'about') {
                  path = '/about-us';
                }

                dbNavItems.push({
                  name: displayName,
                  path: path,
                  order
                });
              });
            }

            // Add feature-specific routes when enabled
            if (features.news?.enabled) {
              dbNavItems.push({ name: 'News', path: '/news', order: 40 });
            }
            if (features.events?.enabled) {
              dbNavItems.push({ name: 'Events', path: '/events', order: 45 });
            }
            if (features.gallery?.enabled) {
              dbNavItems.push({ name: 'Gallery', path: '/gallery', order: 50 });
            }

            dbNavItems.sort((a, b) => {
              if (a.order !== b.order) return a.order - b.order;
              return a.name.localeCompare(b.name);
            });
            
            setNavigationItems(dbNavItems);
          }
        } catch (err) {
          setNavigationItems(getFallbackNavigation());
          setError('Unexpected error loading navigation');
        } finally {
          setLoading(false);
        }
      };

      fetchNavigationItems();
    }
  };
};

/**
 * Fallback navigation for when database is unavailable
 * Maintains basic functionality even without database connection
 * Includes feature-specific routes when features are enabled
 */
function getFallbackNavigation(features: any): NavigationItem[] {
  const nav: NavigationItem[] = [
    { name: 'Home', path: '/', order: 0 },
    { name: 'About', path: '/about-us', order: 10 },
    { name: 'Services', path: '/services', order: 20 }
  ];

  // Add feature-specific routes when enabled
  if (features.news?.enabled) {
    nav.push({ name: 'News', path: '/news', order: 40 });
  }
  if (features.events?.enabled) {
    nav.push({ name: 'Events', path: '/events', order: 45 });
  }
  if (features.gallery?.enabled) {
    nav.push({ name: 'Gallery', path: '/gallery', order: 50 });
  }

  nav.push({ name: 'Contact', path: '/contact', order: 70 });

  return nav;
}
